import { type CSSVariablesResolver } from "@mantine/core"

/**
 * Sets or overrides Mantine CSS variables based on theme values.
 * See https://mantine.dev/styles/css-variables/#css-variables-resolver for details.
 * @param theme
 */
export const cssVariableResolver: CSSVariablesResolver = (theme) => ({
	variables: {
		"--sos-section-top-height": theme.other.sosTopHeight,
		"--sos-section-max-height": theme.other.sosMaxHeight,
		"--table-border-transparent": theme.colors.translucentGray[11],
		"--gradient-headline": theme.other.gradients.headline,
		"--one-stop-pricing-max-box-size": theme.other.oneStopPricingMaxBoxSize,
		"--text-color-disable": theme.colors.royal[108],
		"--shadow-button": theme.shadows.button
	},
	light: {
		// Design System Colors
		"--primary-color": theme.colors.royal[0],
		"--secondary-color": theme.colors.royal[1],
		"--brand-color-royal": theme.colors.royal[2],
		"--brand-color-celebrity": theme.colors.royal[3],
		"--brand-color-silversea": theme.colors.royal[4],
		"--text-color-primary": theme.colors.royal[5],
		"--text-color-secondary": theme.colors.royal[6],
		"--text-color-ternary": theme.colors.royal[5],
		"--text-color-headline-gradient": theme.other.gradients.headline,
		"--text-color-white": theme.colors.royal[6],
		"--text-color-icon": theme.colors.royal[8],
		"--text-color-grey": theme.colors.royal[21],
		"--text-color-alternative": theme.colors.royal[5],
		"--text-color-white-primary": theme.colors.royal[6],
		"--text-color-card-headline": theme.colors.royal[9],
		"--text-color-card-sub-heading": theme.colors.royal[5],
		"--text-color-card-price": theme.colors.royal[57],
		"--text-color-card-details": theme.colors.royal[102],
		"--text-color-card-details-secondary": theme.colors.royal[9],
		"--text-color-cell-highlight": theme.colors.royal[30],
		"--info-card-border-color-icon": theme.colors.royal[29],
		"--navigation-color-primary": theme.colors.royal[6],
		"--navigation-color-secondary": theme.colors.royal[7],
		"--navigation-color-ternary": theme.colors.royal[8],
		"--background-color-primary": theme.colors.royal[10],
		"--background-color-secondary": theme.colors.royal[1],
		"--background-color-ternary": theme.colors.royal[10],
		"--background-color-card": theme.colors.royal[6],
		"--background-color-secondary-card": theme.colors.royal[101],
		"--background-color-ternary-card": theme.colors.royal[106],
		"--shadow-dark-card": theme.colors.royal[104],
		"--background-color-login": theme.colors.royal[10],
		"--background-color-gradient": theme.other.gradients.background,
		"--background-gradient-title": theme.other.gradients["title-background"],
		"--table-gradient-title": theme.other.gradients["table-headline"],
		"--background-color-dropdown": theme.colors.royal[6],
		"--background-gradient-badge": theme.other.gradients["badge-background"],
		"--icon-color": theme.colors.royal[5],
		"--button-color-primary": theme.colors.royal[5],
		"--button-color-border": theme.colors.royal[24],
		"--button-text-color": theme.colors.royal[6],
		"--checkbox-color": theme.colors.royal[57],
		"--table-header-color": theme.colors.royal[5],
		"--table-row-primary-color": theme.colors.royal[6],
		"--table-row-secondary-color": theme.colors.royal[25],
		"--focus-ring-color-outer": theme.colors.royal[5],
		"--focus-ring-color-inner": theme.colors.royal[0],
		"--sos-section-background": theme.other.gradients["sos-background"],
		"--pill-background-color": theme.colors.royal[61],
		"--pill-border-color": theme.colors.royal[60],
		"--character-count-color": theme.colors.royal[119],
		"--promo-section-background": theme.colors.royal[16],
		"--directional-text-color": theme.colors.royal[44],
		"--success-title-color-secondary": theme.colors.royal[49],
		"--text-color-translucent": theme.colors.translucentGray[9],
		"--background-color-sailing-request": theme.colors.royal[25],
		"--background-color-tiny-message-box": theme.colors.royal[122],
		"--border-color-tiny-message-box": theme.colors.royal[123],
		"--background-color-table-body": theme.colors.royal[124],
		"--text-color-empty-message": theme.colors.royal[125],
		"--sidebar-separator-color": theme.colors.royal[25],
		"--sailing-color": theme.colors.royal[8],
		"--cruise-tour-color": theme.colors.royal[73],
		// Design System Colors

		// Old colors
		"--mantine-color-body": theme.colors.royal[10],
		"--mantine-color-text": theme.colors.royal[5],
		"--mantine-primary-color-contrast": theme.colors.royal[0],
		"--nav-bg-color": theme.white,
		"--icon-hover-color": theme.colors.royal[19],
		"--dropdown-bg": theme.white
	},
	dark: {
		// Design System Colors
		"--primary-color": theme.colors.royal[0],
		"--secondary-color": theme.colors.royal[1],
		"--brand-color-royal": theme.colors.royal[2],
		"--brand-color-celebrity": theme.colors.royal[3],
		"--brand-color-silversea": theme.colors.royal[4],
		"--text-color-primary": theme.colors.royal[83],
		"--text-color-secondary": theme.colors.royal[1],
		"--text-color-ternary": theme.colors.royal[12],
		"--text-color-headline-gradient": theme.colors.royal[1],
		"--text-color-white": theme.colors.royal[6],
		"--text-color-grey": theme.colors.royal[12],
		"--text-color-icon": theme.colors.royal[14],
		"--text-color-alternative": theme.colors.royal[6],
		"--text-color-white-primary": theme.colors.royal[12],
		"--text-color-card-headline": theme.colors.royal[1],
		"--text-color-card-sub-heading": theme.colors.royal[1],
		"--text-color-card-price": theme.colors.royal[37],
		"--text-color-card-details": theme.colors.royal[70],
		"--text-color-card-details-secondary": theme.colors.royal[71],
		"--info-card-border-color-icon": theme.colors.royal[28],
		"--navigation-color-primary": theme.colors.royal[15],
		"--navigation-color-secondary": theme.colors.royal[7],
		"--navigation-color-ternary": theme.colors.royal[8],
		"--background-color-primary": theme.colors.royal[16],
		"--background-color-secondary": theme.colors.royal[17],
		"--text-color-cell-highlight": theme.colors.royal[36],
		"--background-color-ternary": theme.colors.royal[17],
		"--background-color-card": theme.colors.royal[99],
		"--background-color-secondary-card": theme.colors.royal[100],
		"--background-color-ternary-card": theme.colors.royal[98],
		"--shadow-dark-card": theme.colors.royal[104],
		"--background-color-login": theme.colors.royal[0],
		"--background-color-gradient": theme.other.gradients.background,
		"--background-gradient-title": theme.other.gradients["title-background"],
		"--table-gradient-title": theme.colors.royal[1],
		"--background-color-dropdown": theme.colors.royal[98],
		"--background-gradient-badge": theme.other.gradients["badge-background"],
		"--icon-color": theme.colors.royal[18],
		"--button-color-primary": theme.colors.royal[6],
		"--button-color-border": theme.colors.royal[76],
		"--button-text-color": theme.colors.royal[5],
		"--checkbox-text-color": theme.colors.royal[70],
		"--table-header-color": theme.colors.royal[99],
		"--table-row-primary-color": theme.colors.royal[100],
		"--table-row-secondary-color": theme.colors.royal[99],
		"--focus-ring-color-outer": theme.colors.royal[1],
		"--focus-ring-color-inner": theme.colors.royal[7],
		"--sos-section-background": theme.other.gradients["sos-background"],
		"--character-count-color": theme.colors.royal[21],
		"--pill-background-color": theme.colors.royal[6],
		"--promo-section-background": theme.colors.royal[98],
		"--directional-text-color": theme.colors.royal[71],
		"--success-title-color-secondary": theme.colors.royal[1],
		"--text-color-translucent": theme.colors.royal[107],
		"--background-color-sailing-request": theme.colors.royal[25],
		"--background-color-tiny-message-box": theme.colors.royal[17],
		"--border-color-tiny-message-box": theme.colors.royal[99],
		"--text-color-empty-message": theme.colors.royal[6],
		"--sidebar-separator-color": theme.colors.royal[76],

		"--sailing-color": theme.colors.royal[36],
		"--cruise-tour-color": theme.colors.royal[63],
		// Design System Colors

		// Old colors
		"--mantine-color-body": theme.colors.royal[98],
		"--mantine-color-text": theme.colors.royal[6],
		"--mantine-primary-color-contrast": theme.colors.royal[33],
		"--nav-bg-color": theme.colors.royal[100],
		"--icon-hover-color": theme.colors.royal[17],
		"--dropdown-bg": theme.colors.royal[17]
	}
})
